/**
 * Manager Recurring Schedules List Component
 * Shows recurring schedules assigned to this manager; allows assigning to their staff
 */

import { useState, useEffect } from 'react';
import type { UserProfile } from '../../../../shared/types';
import {
  getManagerSchedules,
  assignRecurringSchedule,
  removeStaffFromSchedule,
  type RecurringScheduleWithId,
} from '../../services/recurringSchedule.service';
import { getManagerStaff } from '../../services/user.service';
import { getUserNameById } from '@shared/utils/formatting';
import { getErrorMessage } from '@shared/utils/error';
import { normalizeCompanyId } from '@shared/utils/firestore';
import { ErrorMessage } from '../ErrorMessage';
import { ItemCard } from '../ItemCard';
import { EMPTY_STATE_STYLE } from '../../styles/components';

interface ManagerRecurringSchedulesListProps {
  managerId: string;
  managerProfile?: UserProfile;
  refreshTrigger?: number;
  companyTimezone?: string;
}

export function ManagerRecurringSchedulesList({
  managerId,
  managerProfile,
  refreshTrigger,
  companyTimezone,
}: ManagerRecurringSchedulesListProps) {
  const [schedules, setSchedules] = useState<RecurringScheduleWithId[]>([]);
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assigningScheduleId, setAssigningScheduleId] = useState<string | null>(null);

  const companyId = normalizeCompanyId(managerProfile?.companyId);

  const loadData = async () => {
    if (!companyId) {
      setError('No company assigned to this manager profile.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [schedulesData, staffData] = await Promise.all([
        getManagerSchedules(managerId),
        getManagerStaff(managerId),
      ]);
      setSchedules(schedulesData);
      setStaff(staffData);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to load recurring schedules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [managerId, companyId, refreshTrigger]);

  const handleAssignToStaff = async (scheduleId: string, staffId: string) => {
    setAssigningScheduleId(scheduleId);
    try {
      await assignRecurringSchedule({
        scheduleId,
        companyId: companyId!,
        assignedManagerId: managerId,
        userIds: [staffId],
      });
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to assign schedule to staff');
    } finally {
      setAssigningScheduleId(null);
    }
  };

  const handleUnassignStaff = async (scheduleId: string, staffId: string) => {
    setAssigningScheduleId(scheduleId);
    try {
      await removeStaffFromSchedule(scheduleId, companyId!, staffId);
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to remove staff from schedule');
    } finally {
      setAssigningScheduleId(null);
    }
  };

  const handleUnassignMyStaff = async (scheduleId: string, staffIds: string[]) => {
    setAssigningScheduleId(scheduleId);
    try {
      await Promise.all(staffIds.map((staffId) => removeStaffFromSchedule(scheduleId, companyId!, staffId)));
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to unassign staff from schedule');
    } finally {
      setAssigningScheduleId(null);
    }
  };

  const getUserName = (userId: string) => {
    if (managerProfile && userId === managerId) {
      return `${managerProfile.firstName} ${managerProfile.lastName}`;
    }
    return getUserNameById(userId, staff, 'Unknown');
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>Loading recurring schedules...</div>;
  }

  return (
    <div>
      {error && <ErrorMessage message={error} />}

      {schedules.length === 0 ? (
        <div style={EMPTY_STATE_STYLE}>
          No recurring schedules assigned to you yet. Your company administrator will assign schedules to you.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {schedules.map((schedule) => (
            <ItemCard
              key={schedule.id}
              item={schedule}
              viewerRole="manager"
              availableUsers={staff}
              onAssignToStaff={handleAssignToStaff}
              onUnassignStaff={handleUnassignStaff}
              onUnassignMyStaff={handleUnassignMyStaff}
              isAssigning={assigningScheduleId === schedule.id}
              getUserName={getUserName}
              companyTimezone={companyTimezone}
            />
          ))}
        </div>
      )}
    </div>
  );
}
